import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  DocumentChartBarIcon,
  CalendarDaysIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  SparklesIcon,
  ChartPieIcon,
  CursorArrowRaysIcon,
  BoltIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Alert } from '@/components/ui/Alert';
import { gpsApi, GPSDevice, VehicleTracking } from '@/api/gpsApi';
import { getVehicles, Vehicle } from '@/api/fleetApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import toast from 'react-hot-toast';

interface ReportMetrics {
  totalDistance: number;
  totalFuelUsed: number;
  averageSpeed: number;
  drivingTime: number;
  idleTime: number;
  maxSpeed: number;
  harshEvents: number;
  alarmCount: number;
}

interface VehicleReport extends Vehicle {
  gpsDeviceId?: string;
  gpsDevice?: GPSDevice;
  metrics: ReportMetrics;
  efficiency: number;
  score: number;
}

interface ChartData {
  name: string;
  value: number;
  distance?: number;
  speed?: number;
  alarms?: number;
  efficiency?: number;
}

const GPSReportsPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | 'custom'>('week');
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [reportType, setReportType] = useState<'overview' | 'detailed' | 'analytics'>('overview');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Calculate time ranges
  const getTimeRange = () => {
    const endtime = Math.floor(Date.now() / 1000);
    let begintime: number;

    switch (selectedPeriod) {
      case 'day':
        begintime = endtime - (24 * 60 * 60);
        break;
      case 'week':
        begintime = endtime - (7 * 24 * 60 * 60);
        break;
      case 'month':
        begintime = endtime - (30 * 24 * 60 * 60);
        break;
      case 'custom':
        begintime = Math.floor(new Date(customDateRange.startDate).getTime() / 1000);
        break;
      default:
        begintime = endtime - (7 * 24 * 60 * 60);
    }

    return { begintime, endtime: selectedPeriod === 'custom' ? Math.floor(new Date(customDateRange.endDate).getTime() / 1000) : endtime };
  };

  // Fetch fleet vehicles
  const { data: fleetData, isLoading: fleetLoading } = useQuery({
    queryKey: ['reports-fleet'],
    queryFn: () => getVehicles()
  });

  // Fetch GPS devices
  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ['reports-gps-devices'],
    queryFn: () => gpsApi.getDeviceList()
  });

  // Fetch mileage reports
  const { data: mileageData, isLoading: mileageLoading } = useQuery({
    queryKey: ['mileage-report', selectedPeriod, selectedVehicles],
    queryFn: async () => {
      const vehicles = fleetData?.data?.vehicles || [];
      const gpsVehicles = vehicles.filter((v: Vehicle) => 
        v.isTracking && v.engineNumber?.startsWith('GPS:')
      );
      
      if (gpsVehicles.length === 0) return [];
      
      const imeis = selectedVehicles.length > 0 
        ? selectedVehicles 
        : gpsVehicles.map((v: Vehicle) => v.engineNumber!.replace('GPS:', ''));
      
      const { begintime, endtime } = getTimeRange();
      return gpsApi.getMileageReport(imeis, begintime, endtime);
    },
    enabled: !!fleetData?.data?.vehicles
  });

  // Fetch alarm summary
  const { data: alarmSummary, isLoading: alarmLoading } = useQuery({
    queryKey: ['alarm-summary', selectedPeriod],
    queryFn: async () => {
      const vehicles = fleetData?.data?.vehicles || [];
      const gpsVehicles = vehicles.filter((v: Vehicle) => 
        v.isTracking && v.engineNumber?.startsWith('GPS:')
      );
      
      if (gpsVehicles.length === 0) return [];
      
      const { begintime, endtime } = getTimeRange();
      
      const alarmPromises = gpsVehicles.map((vehicle: Vehicle) => {
        const imei = vehicle.engineNumber!.replace('GPS:', '');
        return gpsApi.getDeviceAlarms(imei, begintime, endtime)
          .then(response => ({
            vehicleNumber: vehicle.vehicleNumber,
            imei,
            alarms: response?.data?.alarmRecords || []
          }))
          .catch(() => ({
            vehicleNumber: vehicle.vehicleNumber,
            imei,
            alarms: []
          }));
      });
      
      return Promise.all(alarmPromises);
    },
    enabled: !!fleetData?.data?.vehicles
  });

  // Process vehicles with GPS data
  const vehiclesWithGPS = React.useMemo(() => {
    const vehicles = fleetData?.data?.vehicles || [];
    const gpsDevices = devicesData?.data?.data?.devices || [];
    
    return vehicles
      .filter((v: Vehicle) => v.isTracking && v.engineNumber?.startsWith('GPS:'))
      .map((vehicle: Vehicle) => {
        const gpsDeviceId = vehicle.engineNumber!.replace('GPS:', '');
        const gpsDevice = gpsDevices.find((d: GPSDevice) => d.imei === gpsDeviceId);
        
        // Get alarm count from alarm summary
        const alarmCount = alarmSummary?.find(a => a.imei === gpsDeviceId)?.alarms?.length || 0;

        // Initialize metrics with zeros - no mock data
        const metrics: ReportMetrics = {
          totalDistance: 0,
          totalFuelUsed: 0,
          averageSpeed: 0,
          drivingTime: 0,
          idleTime: 0,
          maxSpeed: 0,
          harshEvents: 0,
          alarmCount
        };

        const efficiency = 0;
        const score = Math.max(100 - (alarmCount * 3), 0);
        
        return {
          ...vehicle,
          gpsDeviceId,
          gpsDevice,
          metrics,
          efficiency,
          score
        } as VehicleReport;
      });
  }, [fleetData, devicesData, alarmSummary]);

  // Chart data preparation
  const chartData: ChartData[] = vehiclesWithGPS.map(vehicle => ({
    name: vehicle.vehicleNumber,
    value: vehicle.metrics.totalDistance,
    distance: vehicle.metrics.totalDistance,
    speed: vehicle.metrics.averageSpeed,
    alarms: vehicle.metrics.alarmCount,
    efficiency: vehicle.efficiency
  }));

  const pieData = [
    { name: 'Driving', value: vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.drivingTime, 0), color: '#10B981' },
    { name: 'Idle', value: vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.idleTime, 0), color: '#F59E0B' },
  ];

  const exportReport = () => {
    const reportData = {
      period: selectedPeriod,
      dateRange: getTimeRange(),
      vehicles: vehiclesWithGPS.map(v => ({
        vehicleNumber: v.vehicleNumber,
        make: v.make,
        model: v.vehicleModel,
        ...v.metrics,
        efficiency: v.efficiency,
        score: v.score
      })),
      summary: {
        totalVehicles: vehiclesWithGPS.length,
        totalDistance: vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.totalDistance, 0),
        totalFuel: vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.totalFuelUsed, 0),
        avgEfficiency: vehiclesWithGPS.reduce((sum, v) => sum + v.efficiency, 0) / vehiclesWithGPS.length,
        totalAlarms: vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.alarmCount, 0)
      }
    };
    
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gps-fleet-report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  if (fleetLoading || devicesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-slate-600 mt-4">Generating GPS fleet reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
                <DocumentChartBarIcon className="w-10 h-10 text-blue-600 mr-4" />
                GPS Fleet Reports
              </h1>
              <p className="text-slate-600 mt-2 text-lg">
                Comprehensive analytics and insights for your GPS-enabled fleet
              </p>
            </div>
            
            <Button
              onClick={exportReport}
              variant="primary"
              className="bg-blue-600 hover:bg-blue-700"
            >
              <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Report Period</label>
                <div className="flex bg-slate-100 rounded-lg p-1">
                  {(['day', 'week', 'month', 'custom'] as const).map((period) => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        selectedPeriod === period
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {selectedPeriod === 'custom' && (
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="date"
                    value={customDateRange.startDate}
                    onChange={(e) => setCustomDateRange(prev => ({...prev, startDate: e.target.value}))}
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                  <span className="text-slate-500">to</span>
                  <input
                    type="date"
                    value={customDateRange.endDate}
                    onChange={(e) => setCustomDateRange(prev => ({...prev, endDate: e.target.value}))}
                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Report Type</label>
              <div className="flex bg-slate-100 rounded-lg p-1">
                {(['overview', 'detailed', 'analytics'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setReportType(type)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      reportType === type
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TruckIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-slate-500 text-sm">Active Vehicles</p>
                <p className="text-2xl font-bold text-slate-900">{vehiclesWithGPS.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <MapPinIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-slate-500 text-sm">Total Distance</p>
                <p className="text-2xl font-bold text-slate-900">
                  {vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.totalDistance, 0).toLocaleString()} km
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-slate-500 text-sm">Fleet Efficiency</p>
                <p className="text-2xl font-bold text-slate-900">
                  {vehiclesWithGPS.length > 0 
                    ? Math.round(vehiclesWithGPS.reduce((sum, v) => sum + v.efficiency, 0) / vehiclesWithGPS.length * 10) / 10
                    : 0
                  } km/L
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-slate-500 text-sm">Total Alerts</p>
                <p className="text-2xl font-bold text-slate-900">
                  {vehiclesWithGPS.reduce((sum, v) => sum + v.metrics.alarmCount, 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {reportType === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Distance Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 text-blue-600 mr-2" />
                Distance by Vehicle
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Bar dataKey="distance" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Speed Analytics */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <CursorArrowRaysIcon className="w-5 h-5 text-green-600 mr-2" />
                Average Speed Analysis
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="speed" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {reportType === 'analytics' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Fuel Efficiency */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <ChartPieIcon className="w-5 h-5 text-purple-600 mr-2" />
                Fuel Efficiency Distribution
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="efficiency" 
                      stroke="#8b5cf6" 
                      fill="#8b5cf6"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Driving vs Idle Time */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 text-orange-600 mr-2" />
                Fleet Time Distribution
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color || '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Performance Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                <ChartBarIcon className="w-5 h-5 text-slate-600 mr-2" />
                Vehicle Performance Report
              </h3>
              <div className="text-sm text-slate-500">
                {selectedPeriod === 'custom' 
                  ? `${customDateRange.startDate} to ${customDateRange.endDate}`
                  : `Last ${selectedPeriod}`
                }
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Distance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Speed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Efficiency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Drive Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Alarms</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {vehiclesWithGPS.map((vehicle: any) => (
                  <tr key={vehicle.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <TruckIcon className="w-5 h-5 text-slate-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{vehicle.vehicleNumber}</div>
                          <div className="text-sm text-slate-500">{vehicle.make} {vehicle.vehicleModel}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900">{vehicle.metrics.totalDistance} km</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{vehicle.metrics.averageSpeed} km/h</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900">{vehicle.efficiency} km/L</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{Math.round(vehicle.metrics.drivingTime / 60)}h {vehicle.metrics.drivingTime % 60}m</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        vehicle.metrics.alarmCount === 0 
                          ? 'bg-green-100 text-green-800'
                          : vehicle.metrics.alarmCount <= 3
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {vehicle.metrics.alarmCount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-slate-900 mr-2">{vehicle.score}/100</div>
                        <div className="w-16 bg-slate-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              vehicle.score >= 80 ? 'bg-green-500' : 
                              vehicle.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${vehicle.score}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Reports Section */}
        {reportType === 'detailed' && (
          <div className="space-y-8">
            {vehiclesWithGPS.map((vehicle: any) => (
              <div key={vehicle.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900">{vehicle.vehicleNumber}</h3>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      vehicle.score >= 80 ? 'bg-green-100 text-green-800' : 
                      vehicle.score >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      Performance: {vehicle.score}/100
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{vehicle.metrics.totalDistance}</div>
                    <div className="text-sm text-slate-500">Distance (km)</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{vehicle.metrics.averageSpeed}</div>
                    <div className="text-sm text-slate-500">Avg Speed</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{vehicle.metrics.maxSpeed}</div>
                    <div className="text-sm text-slate-500">Max Speed</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{vehicle.efficiency}</div>
                    <div className="text-sm text-slate-500">Efficiency</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{vehicle.metrics.harshEvents}</div>
                    <div className="text-sm text-slate-500">Harsh Events</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-slate-900">{Math.round(vehicle.metrics.drivingTime / 60)}</div>
                    <div className="text-sm text-slate-500">Drive Hours</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Data Message */}
        {vehiclesWithGPS.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <DocumentChartBarIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No GPS Fleet Data</h3>
            <p className="text-slate-500">
              No GPS-enabled vehicles found. Ensure vehicles have GPS devices configured.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GPSReportsPage;